// =====================================================
// HOOK: useKpiDetail — lignes détaillées d'une carte KPI (issue #228)
// =====================================================
// Exécute le descripteur produit par `buildKpiDetailQuery` (lib/dashboard-stats/
// kpi-detail.ts) et normalise le résultat en KpiDetailRow[]. Les prédicats sont
// le MIROIR de compute.ts : même source, même scope édition, même fenêtre — le
// total renvoyé (count exact) doit donc égaler la valeur de la carte.
//
// Chargement paresseux : rien n'est requêté tant que la modale n'est pas ouverte
// (`query === null` → état vide). Le dashboard charge déjà beaucoup au montage,
// on n'ajoute pas 4 requêtes de plus pour un détail que personne n'ouvrira.

import { useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';
import {
  KPI_DETAIL_LIMIT,
  type KpiDetailQuery,
  type KpiDetailRow,
  type KpiKey,
} from '@/lib/dashboard-stats';
import { ROUTES } from '@/lib/routes';

interface UseKpiDetailResult {
  rows: KpiDetailRow[];
  /** Total réel (count exact), potentiellement > rows.length si tronqué. */
  total: number;
  loading: boolean;
  error: string | null;
  /**
   * Carte dont le résultat est effectivement en état. L'effet ne s'exécutant
   * qu'APRÈS le premier render, `loading` y vaut encore false : sans ce témoin,
   * l'UI afficherait un « aucune ligne » (et un faux écart carte ↔ détail) le
   * temps d'un frame. Comparer `loadedKey` à la carte ouverte lève l'ambiguïté.
   */
  loadedKey: KpiKey | null;
}

/** « Prénom Nom » compacté, ou null si les deux champs sont vides. */
function fullName(first?: string | null, last?: string | null): string | null {
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || null;
}

export function useKpiDetail(query: KpiDetailQuery | null): UseKpiDetailResult {
  const supabase = useSupabase();
  const [rows, setRows] = useState<KpiDetailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<KpiKey | null>(null);

  useEffect(() => {
    if (!query) {
      setRows([]);
      setTotal(0);
      setError(null);
      setLoadedKey(null);
      return;
    }

    let cancelled = false;

    async function run(q: KpiDetailQuery) {
      setLoading(true);
      setError(null);
      try {
        const result =
          q.source === 'payments'
            ? await fetchPayments(q)
            : q.source === 'crews'
              ? await fetchCrews(q)
              : q.source === 'contacts'
                ? await fetchContacts(q)
                : await fetchSubmissions(q);
        if (cancelled) return;
        setRows(result.rows);
        setTotal(result.total);
        setLoadedKey(q.key);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : 'Erreur inconnue');
        setLoadedKey(q.key);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    // --- payment_cases scope=bpi status=paid ----------------------------------
    // Plus rattaché à aucune carte depuis la réunion 22/07 (BPI = tous les BPI
    // générés, cf. fetchSubmissions) ; conservé pour la source 'payments'.
    async function fetchPayments(q: KpiDetailQuery) {
      const {
        data,
        count,
        error: err,
      } = await supabase
        .from('payment_cases')
        .select(
          // Deux FK vers contacts (contact_id + payer_contact_id) → l'embed doit
          // nommer la contrainte, sinon PostgREST renvoie une erreur d'ambiguïté.
          'id, order_id, created_at, amount_cents, method, crew_id, ' +
            'crew:crews(crew_number, name), ' +
            'payer:contacts!payment_cases_payer_contact_id_fkey(first_name, last_name)',
          { count: 'exact' }
        )
        .eq('scope', 'bpi')
        .eq('status', 'paid')
        .eq('product_id', q.editionId!)
        .gte('created_at', q.startIso)
        .lte('created_at', q.endIso)
        .order('created_at', { ascending: false })
        .limit(KPI_DETAIL_LIMIT);
      if (err) throw err;

      const rows: KpiDetailRow[] = (data ?? []).map((row: any) => ({
        id: row.id,
        primary:
          row.crew?.name ||
          fullName(row.payer?.first_name, row.payer?.last_name) ||
          'Payeur non identifié',
        secondary: row.order_id ?? null,
        status: row.method ?? null,
        amountCents: row.amount_cents ?? null,
        createdAt: row.created_at,
        href: row.crew_id ? ROUTES.DATA.CREWS.DETAIL(row.crew_id) : null,
      }));
      return { rows, total: count ?? rows.length };
    }

    // --- PI / ID / Dérogations : crews (cf. countPi/countId/countDerogations) -
    async function fetchCrews(q: KpiDetailQuery) {
      let request = supabase
        .from('crews')
        .select('id, crew_number, name, status, created_at', { count: 'exact' })
        .eq('product_id', q.editionId!)
        .gte('created_at', q.startIso)
        .lte('created_at', q.endIso);
      if (q.crewStatuses?.length) request = request.in('status', q.crewStatuses);
      if (q.derogationPendingOnly) request = request.eq('derogation_pending', true);
      if (q.hasDerogationOnly) request = request.eq('has_derogation', true);

      const {
        data,
        count,
        error: err,
      } = await request.order('created_at', { ascending: false }).limit(KPI_DETAIL_LIMIT);
      if (err) throw err;

      const rows: KpiDetailRow[] = (data ?? []).map((row: any) => ({
        id: row.id,
        primary: row.name || 'Équipage sans nom',
        secondary: row.crew_number ? `#${row.crew_number}` : null,
        status: row.status ?? null,
        amountCents: null,
        createdAt: row.created_at,
        href: ROUTES.DATA.CREWS.DETAIL(row.id),
      }));
      return { rows, total: count ?? rows.length };
    }

    // --- PR : contacts.status='prospect', GLOBAL (cf. countPr) ----------------
    async function fetchContacts(q: KpiDetailQuery) {
      const {
        data,
        count,
        error: err,
      } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, status, created_at', { count: 'exact' })
        .eq('status', 'prospect')
        .gte('created_at', q.startIso)
        .lte('created_at', q.endIso)
        .order('created_at', { ascending: false })
        .limit(KPI_DETAIL_LIMIT);
      if (err) throw err;

      const rows: KpiDetailRow[] = (data ?? []).map((row: any) => ({
        id: row.id,
        primary: fullName(row.first_name, row.last_name) || 'Contact sans nom',
        secondary: row.email ?? null,
        status: row.status ?? null,
        amountCents: null,
        createdAt: row.created_at,
        href: ROUTES.DATA.CONTACTS.DETAIL(row.id),
      }));
      return { rows, total: count ?? rows.length };
    }

    // --- BPI / BPI en attente : form_submissions des formulaires generates_bpi
    // (cf. countBpi / countPiAttente). `unconvertedOnly` restreint aux non
    // converties. Le scope édition passe par le FORMULAIRE (forms.product_id),
    // exactement comme le hook du dashboard qui construit formProductMap.
    async function fetchSubmissions(q: KpiDetailQuery) {
      const { data: forms, error: formsErr } = await supabase
        .from('forms')
        .select('id')
        .eq('generates_bpi', true)
        .eq('product_id', q.editionId!);
      if (formsErr) throw formsErr;

      const formIds = (forms ?? []).map((f: any) => f.id);
      if (formIds.length === 0) return { rows: [], total: 0 };

      let request = supabase
        .from('form_submissions')
        .select(
          'id, created_at, converted_at, bpi_code, contact_id, contact:contacts(first_name, last_name, email)',
          {
            count: 'exact',
          }
        )
        .in('form_id', formIds)
        .gte('created_at', q.startIso)
        .lte('created_at', q.endIso);
      if (q.unconvertedOnly) request = request.is('converted_at', null);

      const {
        data,
        count,
        error: err,
      } = await request.order('created_at', { ascending: false }).limit(KPI_DETAIL_LIMIT);
      if (err) throw err;

      const rows: KpiDetailRow[] = (data ?? []).map((row: any) => ({
        id: row.id,
        primary:
          fullName(row.contact?.first_name, row.contact?.last_name) ||
          row.contact?.email ||
          'Contact inconnu',
        secondary: row.bpi_code ?? null,
        // Colonne « Statut BPI » : la carte BPI mélange converties et non
        // converties, la distinction doit se lire ligne à ligne.
        status: row.converted_at ? 'Converti' : 'Non converti',
        amountCents: null,
        createdAt: row.created_at,
        href: row.contact_id ? ROUTES.DATA.CONTACTS.DETAIL(row.contact_id) : null,
      }));
      return { rows, total: count ?? rows.length };
    }

    run(query);
    return () => {
      cancelled = true;
    };
  }, [query, supabase]);

  return { rows, total, loading, error, loadedKey };
}
